import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, TrendingUp, Clock, AlertTriangle } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { HashDisplay } from '../components/common/HashDisplay';
import { StatCard } from '../components/common/StatCard';
import { getValidators, getNetworkStats } from '../lib/api';
import { formatNumber } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

export default function ValidatorsPage() {
  const [validators, setValidators] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [valRes, statsRes] = await Promise.all([
          getValidators(),
          getNetworkStats(),
        ]);
        setValidators(valRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching validators:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalStaked = validators.reduce((acc, v) => acc + v.stake, 0);
  const activeValidators = validators.filter(v => v.status === 'active').length;
  const avgUptime = validators.length > 0 
    ? validators.reduce((acc, v) => acc + v.uptime, 0) / validators.length 
    : 0;

  const columns = [
    {
      header: 'Rank',
      cell: (row, index) => (
        <span className="font-mono font-bold text-muted-foreground">#{index + 1}</span>
      ),
    },
    {
      header: 'Validator',
      cell: (row) => <HashDisplay hash={row.address} type="address" link={`/address/${row.address}`} />,
    },
    {
      header: 'Status',
      cell: (row) => (
        <Badge 
          variant="outline" 
          className={row.status === 'active' 
            ? 'text-green-400 border-green-400/30' 
            : 'text-red-400 border-red-400/30'}
        >
          {row.status}
        </Badge>
      ),
    },
    {
      header: 'Stake',
      cell: (row) => (
        <div>
          <span className="font-mono">{formatNumber(row.stake, 2)} GG</span>
          <div className="text-xs text-muted-foreground">
            {((row.stake / totalStaked) * 100).toFixed(2)}% of total
          </div>
        </div>
      ),
    },
    {
      header: 'Commission',
      cell: (row) => <span className="font-mono">{(row.commission * 100).toFixed(2)}%</span>,
    },
    {
      header: 'Uptime',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Progress value={row.uptime * 100} className="w-16 h-2" />
          <span className="font-mono text-sm">{(row.uptime * 100).toFixed(2)}%</span>
        </div>
      ),
    },
    {
      header: 'Blocks Validated',
      cell: (row) => <span className="font-mono">{formatNumber(row.blocks_validated, 0)}</span>,
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="validators-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-gradient-to-br from-green-500/20 to-transparent">
            <Shield className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Validators</h1>
            <p className="text-sm text-muted-foreground">
              PoS consensus participants (30% of block rewards)
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Validators"
            value={formatNumber(validators.length, 0)}
            subtitle={`${activeValidators} active`}
            icon={Shield}
            accentColor="success"
          />
          <StatCard
            title="Total Staked"
            value={`${formatNumber(totalStaked, 0)} GG`}
            subtitle={stats ? `${((totalStaked / stats.circulating_supply) * 100).toFixed(1)}% of supply` : ''}
            icon={TrendingUp}
            accentColor="primary"
          />
          <StatCard
            title="Average Uptime"
            value={`${(avgUptime * 100).toFixed(2)}%`}
            icon={Clock}
            accentColor="secondary"
          />
          <StatCard
            title="Min Stake Required"
            value="10,000 GG"
            icon={AlertTriangle}
            accentColor="warning"
          />
        </div>

        {/* Validator Rules */}
        <div className="data-card p-6 mb-8">
          <h2 className="text-lg font-bold mb-4">Validator Requirements</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Min Stake</p>
              <p className="font-mono font-bold">10,000 GG</p>
            </div>
            <div>
              <p className="text-muted-foreground">Unbonding Period</p>
              <p className="font-mono font-bold">21 Days</p>
            </div>
            <div>
              <p className="text-muted-foreground">Double Sign Slash</p>
              <p className="font-mono font-bold text-red-400">5%</p>
            </div>
            <div>
              <p className="text-muted-foreground">Downtime Slash</p>
              <p className="font-mono font-bold text-yellow-400">1%</p>
            </div>
          </div>
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={validators.map((v, i) => ({ ...v, _index: i }))}
          loading={loading}
          emptyMessage="No validators found"
          onRowClick={(row) => navigate(`/address/${row.address}`)}
        />
      </div>
    </Layout>
  );
}
